/**
 * A conta do PAINEL da mesa (quem cuida × em que pé está o caso), sem React.
 *
 * O painel é um mapa, e mapa não se rearruma quando se aponta para ele. Até
 * 24/09 as linhas eram ordenadas pelo total e a matriz vinha do servidor já
 * filtrada pelo grupo escolhido: clicar em "João · Atendendo" zerava as outras
 * colunas, o João subia para o topo e os colegas viravam uma fileira de pontos
 * — o Felipe (áudio de 24/09) queria o contrário: "independente de onde clicar,
 * as linhas com os nomes na ordem permanecem as mesmas e continuam aparecendo
 * todas as outras informações". Por isso:
 *
 *   - a ORDEM é a do nome (a fila primeiro), e nada que o clique muda entra nela;
 *   - a matriz do painel é a INTEIRA — o grupo e a célula são eixos dele, não
 *     recorte (`matrizDaMesa` não filtra por grupo). O clique só marca e
 *     estreita a lista de casos.
 */

/** A chave da fila na URL (`?resp=`); na matriz a fila é `''`. */
export const SEM_RESPONSAVEL = "__fila__";

export const chaveNaMatriz = (chave) => (chave === SEM_RESPONSAVEL ? "" : chave);

/**
 * A matriz só com as etapas do GRUPO escolhido — o que o filtro da coluna da
 * mesa via antes, quando o servidor filtrava. Lá as colunas são ETAPAS e o grupo
 * é recorte grosso; aqui no painel as colunas são os grupos, então o painel lê a
 * matriz inteira e só o filtro recebe esta.
 */
export function matrizDoGrupo(matriz, etapas, grupo) {
  if (!grupo) return matriz;
  const doGrupo = new Set((etapas ?? []).filter((e) => e.grupo === grupo).map((e) => e.chave));
  return matriz.filter((c) => doGrupo.has(c.stage));
}

/** Linhas, colunas e células do painel. A ordem das linhas não depende dos números. */
export function contarPainel({ matriz, etapas, equipe, grupos }) {
  const grupoDe = new Map((etapas ?? []).map((e) => [e.chave, e.grupo]));
  const porCelula = new Map();
  const porResp = new Map();
  const porGrupo = new Map();
  let totalGeral = 0;
  for (const c of matriz ?? []) {
    const g = grupoDe.get(c.stage);
    /* Etapa sem grupo (esquema mexido no meio do caminho) não entra: melhor
       faltar na grade do que aparecer numa coluna errada. */
    if (!g) continue;
    const chave = `${g}|${c.resp}`;
    porCelula.set(chave, (porCelula.get(chave) ?? 0) + c.n);
    porResp.set(c.resp, (porResp.get(c.resp) ?? 0) + c.n);
    porGrupo.set(g, (porGrupo.get(g) ?? 0) + c.n);
    totalGeral += c.n;
  }
  const ativos = new Map((equipe ?? []).map((p) => [p.nome.toUpperCase(), p]));
  /* Toda pessoa ATIVA do cadastro (mesmo em zero — carga zero é informação),
     mais quem tem caso e não está no cadastro. A regra é a mesma do filtro da
     coluna: duas respostas para "quem aparece" seriam duas equipes. */
  const nomes = new Set([...ativos.keys(), ...[...porResp.keys()].filter(Boolean)]);
  const pessoas = [...nomes]
    .map((chave) => {
      const p = ativos.get(chave);
      return { chave, nome: p?.nome ?? chave, foto: p?.avatar, cor: p?.cor, total: porResp.get(chave) ?? 0 };
    })
    .toSorted((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const fila = { chave: SEM_RESPONSAVEL, nome: "Fila", total: porResp.get("") ?? 0 };
  const linhas = [fila, ...pessoas];
  const colunas = (grupos ?? []).map((g) => ({ ...g, total: porGrupo.get(g.chave) ?? 0 }));
  /* O maior de cada coluna é a régua da cor daquela coluna. A fila entra na
     conta: ela é carga de verdade. */
  const maiorPorColuna = new Map(
    colunas.map((g) => [g.chave, Math.max(0, ...linhas.map((p) => porCelula.get(`${g.chave}|${chaveNaMatriz(p.chave)}`) ?? 0))]),
  );
  return { linhas, colunas, porCelula, maiorPorColuna, totalGeral };
}
